<template>
  <div class="apim-noprint">
    <GridRow class="is-flex">
      <GridCol
        v-for="proj in editableProjects"
        :key="proj.id"
        :lg="3"
        :md="4"
        :sm="6"
        :xs="12"
        class="panel-fill"
      >
        <Panel
          :title="'Project ' + proj.name + ': Quick start'"
          :class="{
            'panel-primary': primary,
            'panel-default': !primary,
            'panel-fill': true,
          }"
        >
          <template #default>
            <div class="medium-card-text text-center">
              Add a new API call to project {{ proj.name }}:
              <div>
                <a
                  :class="{
                    btn: true,
                    'btn-link': true,
                    'btn-lg': true,
                    disabled: loading,
                  }"
                  :disabled="loading"
                  @click="onAddAPI(proj)"
                  @keyup.enter="onAddAPI(proj)"
                >
                  <i class="glyphicon glyphicon-plus" /> Add API Call
                </a>
              </div>
            </div>
          </template>
          <template #footer>
            <div class="info-label">
              This will take you to the edit page for your new API in the project
              {{ proj.name }}.
            </div>
          </template>
        </Panel>
      </GridCol>

      <GridCol v-if="!editableProjects.length" :lg="4" :md="6" :sm="6" :xs="12" class="panel-fill">
        <Panel
          title="Want to monitor APIs?"
          :class="{
            'panel-primary': primary,
            'panel-default': !primary,
            'panel-fill': true,
          }"
        >
          <template #default>
            <div class="medium-card-text text-center">
              Do you have an API to monitor? Start by adding a new API call:
              <div>
                <a
                  :class="{
                    btn: true,
                    'btn-link': true,
                    'btn-lg': true,
                    disabled: loading,
                  }"
                  :disabled="loading"
                  @click="onAddAPI(null)"
                  @keyup.enter="onAddAPI(null)"
                >
                  <i class="glyphicon glyphicon-plus" /> Add API Call
                </a>
              </div>
            </div>
          </template>
          <template #footer>
            <div class="info-label">
              This will add a new project and take you to the edit page for your new API.
            </div>
          </template>
        </Panel>
      </GridCol>

      <GridCol
        v-if="editableProjects.length < 4 && editableProjects.length > 0"
        :lg="4"
        :md="6"
        :sm="6"
        :xs="12"
        class="panel-fill"
      >
        <Panel
          title="Create projects"
          :class="{
            'panel-primary': primary,
            'panel-default': !primary,
            'panel-fill': true,
          }"
        >
          <template #default>
            <div class="medium-card-text text-center">
              More APIs to monitor? Start by adding a new project:
              <ProjectCreate
                btn-class="btn-lg"
                @project-created="
                  $router.push({ name: 'project-home', query: { project_key_str: $event } })
                "
              />
            </div>
          </template>
          <template #footer>
            <div class="info-label">
              API calls are organized into Projects. Don't forget to schedule your API calls to make
              sure they're being monitored from around the world.
            </div>
          </template>
        </Panel>
      </GridCol>

      <GridCol :lg="4" :md="6" :sm="6" :xs="12" class="panel-fill">
        <Panel
          title="Want to view data for public APIs?"
          :class="{
            'panel-primary': primary,
            'panel-default': !primary,
            'panel-fill': true,
          }"
        >
          <template #default>
            <div class="medium-card-text text-center">
              Not ready to type in API definitions? View data we've collected:
              <a
                :class="{ btn: true, 'btn-link': true, 'btn-lg': true }"
                :disabled="loading"
                @click="$emit('open-api-expert')"
                @keyup.enter="$emit('open-api-expert')"
              >
                <img
                  style="width: 20px; height: 20px"
                  src="/img/api-expert-icon.svg"
                  alt="API.expert"
                />
                View API.expert Collections
              </a>
            </div>
          </template>
          <template #footer>
            <div class="info-label">
              Our API.expert Collections give you access to view statistics and uptime for popular
              public APIs. Start your free trial now!
            </div>
          </template>
        </Panel>
      </GridCol>
    </GridRow>
  </div>
</template>

<script>
import { call } from 'vuex-pathify';
import Panel from '../Panel.vue';
import ProjectCreate from '../organization/ProjectCreate.vue';
import newCallPayload from '../../js/new_call';

export default {
  name: 'ProjectActions',
  components: {
    Panel,
    ProjectCreate,
  },
  props: {
    primary: {
      type: Boolean,
      required: false,
      default: false,
    },
    projects: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
    };
  },
  computed: {
    editableProjects() {
      return this.projects.filter(
        ({ accessLevel }) => accessLevel === 'OWNER' || accessLevel === 'EDITOR',
      );
    },
  },
  methods: {
    async onAddAPI(project) {
      this.loading = true;
      let proj = null;
      try {
        if (project && project.projectId) {
          proj = { id: project.projectId };
        } else {
          proj = await this.createNoOrgProject({
            name: `API Project ${new Date().toLocaleDateString()}`,
          });
          // We get a ProjectAccess object here, so get project
          proj = proj.project;
        }
        // Add API call
        await this.addCall(proj.id);
      } finally {
        this.loading = false;
      }
    },
    async addCall(projectId) {
      const { id: callId } = await this.createCall({ ...newCallPayload(), projectId });
      this.$router.push({ name: 'calls-edit', params: { callId } });
    },
    createNoOrgProject: call('account2/createProject'),
    createCall: call('apimAPI/createCall'),
  },
};
</script>

<style scoped>
.large-card-text {
  font-size: 2.75rem;
}
.medium-card-text {
  font-size: 1.75rem;
}
small.large-card-text {
  font-size: 1rem;
}
.panel-fill {
  height: 100%;
}
</style>
